import { useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { DropdownItem } from "@/modules/dropdowns/types";
import type {
  DecisionComment,
  DecisionDetail,
  DecisionListItem,
  DecisionVoteResult,
  NewDecisionInput,
  PaginatedResponse,
  UpdateDecisionInput,
} from "../types";

const DECISIONS_KEY = ["decisions"] as const;

export type DecisionListParams = {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  phaseId?: string;
  stageId?: string;
  status?: string;
  category?: string;
  impact?: string;
  visibility?: string;
  isPinned?: boolean;
  tags?: string[];
};

export function useDecisionList(
  params: DecisionListParams = {},
): UseQueryResult<PaginatedResponse<DecisionListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...DECISIONS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<DecisionListItem>>("/decisions", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          meetingId: params.meetingId,
          phaseId: params.phaseId,
          stageId: params.stageId,
          status: params.status,
          category: params.category,
          impact: params.impact,
          visibility: params.visibility,
          tags:
            params.tags && params.tags.length > 0
              ? params.tags.join(",")
              : undefined,
          isPinned:
            typeof params.isPinned === "boolean"
              ? params.isPinned
                ? "true"
                : "false"
              : undefined,
        },
      }),
  });
}

export function useDecision(
  id: string | null | undefined,
): UseQueryResult<DecisionDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...DECISIONS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ decision: DecisionDetail }>(
        `/decisions/${id}`,
        { token: accessToken },
      );
      return res.decision;
    },
  });
}

export function useCreateDecision() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: NewDecisionInput) =>
      apiFetch<{ decision: DecisionDetail }>("/decisions", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY] });
    },
  });
}

export function useUpdateDecision(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateDecisionInput) =>
      apiFetch<{ decision: DecisionDetail }>(`/decisions/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY] });
    },
  });
}

export function useUpdateDecisionStatus(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: string) =>
      apiFetch<{ decision: DecisionDetail }>(`/decisions/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY] });
    },
  });
}

export function useToggleDecisionPin() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    { decision: DecisionDetail },
    Error,
    { id: string; isPinned: boolean }
  >({
    mutationFn: ({ id, isPinned }) =>
      apiFetch<{ decision: DecisionDetail }>(`/decisions/${id}/pin`, {
        method: "PUT",
        body: { isPinned },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY] });
    },
  });
}

export function useDeleteDecision() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/decisions/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY] });
    },
  });
}

export function useVoteDecision(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voteType: "up" | "down") =>
      apiFetch<DecisionVoteResult>(`/decisions/${id}/vote`, {
        method: "POST",
        body: { voteType },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY, "list"] });
    },
  });
}

export function useRemoveDecisionVote(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<DecisionVoteResult>(`/decisions/${id}/vote`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [...DECISIONS_KEY, "list"] });
    },
  });
}

export function useDecisionComments(
  decisionId: string | null | undefined,
): UseQueryResult<DecisionComment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...DECISIONS_KEY,
      "comments",
      decisionId,
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!decisionId,
    queryFn: async () => {
      const res = await apiFetch<{ data: DecisionComment[] }>(
        `/decisions/${decisionId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddDecisionComment(decisionId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: DecisionComment }>(
        `/decisions/${decisionId}/comments`,
        {
          method: "POST",
          body: { content },
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...DECISIONS_KEY, "comments", decisionId],
      });
    },
  });
}

export function useDeleteDecisionComment(decisionId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/decisions/${decisionId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...DECISIONS_KEY, "comments", decisionId],
      });
    },
  });
}

export function useLinkIdea(decisionId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ideaId: string) =>
      apiFetch<{ decision: DecisionDetail }>(
        `/decisions/${decisionId}/link-idea`,
        {
          method: "POST",
          body: { ideaId },
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...DECISIONS_KEY, "detail", decisionId],
      });
    },
  });
}

export function useUnlinkIdea(decisionId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ideaId: string) =>
      apiFetch<{ decision: DecisionDetail }>(
        `/decisions/${decisionId}/link-idea/${ideaId}`,
        {
          method: "DELETE",
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...DECISIONS_KEY, "detail", decisionId],
      });
    },
  });
}

export function useDecisionCategories(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("decision_category");
}

export function useDecisionImpacts(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("decision_impact");
}

export function useDecisionStatuses(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("decision_status");
}

export function useDecisionTags(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("decision_tag");
}

type AutoSaveState = {
  isSaving: boolean;
  lastSaved: Date | null;
  isDirty: boolean;
};

export function useAutoSaveDecision(
  decisionId: string,
  title: string,
  content: string,
  enabled: boolean,
): AutoSaveState & { saveNow: () => Promise<void> } {
  const update = useUpdateDecision(decisionId);
  const [state, setState] = useState<AutoSaveState>({
    isSaving: false,
    lastSaved: null,
    isDirty: false,
  });
  const initial = useRef<{ title: string; content: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (initial.current === null) {
      initial.current = { title, content };
      return;
    }
    const dirty =
      title !== initial.current.title || content !== initial.current.content;
    setState((s) => ({ ...s, isDirty: dirty }));
    if (!dirty) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void doSave();
    }, 2000);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, enabled]);

  useEffect(() => {
    if (!enabled) {
      initial.current = null;
      setState({ isSaving: false, lastSaved: null, isDirty: false });
    }
  }, [enabled]);

  async function doSave() {
    if (!decisionId) return;
    setState((s) => ({ ...s, isSaving: true }));
    try {
      await update.mutateAsync({ title, content });
      initial.current = { title, content };
      setState({ isSaving: false, lastSaved: new Date(), isDirty: false });
    } catch {
      setState((s) => ({ ...s, isSaving: false }));
    }
  }

  async function saveNow() {
    if (timer.current) clearTimeout(timer.current);
    await doSave();
  }

  return { ...state, saveNow };
}
